import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { MdLink, MdLinkOff } from "react-icons/md";
import { TbInfoCircle, TbAlertCircle, TbChevronDown, TbChevronRight, TbClock, TbCreditCardPay, TbPlus, TbTrash } from "react-icons/tb";
import { Section, SettingRow, Toggle, SaveButton } from "./SettingsControls";
import { useSumup, SumupTarget, SumupSwitch, SumupConnectionSummary, SumupReader } from "src/hooks/useSumup";
import { ConfirmPopup } from "src/components/Popup/ConfirmPopup";

type Draft = { usePraxis: boolean } & Record<SumupSwitch, boolean>;

/**
 * Settings page "Kartenzahlung (SumUp)": the doctor's (or the praxis's) own SumUp account, card
 * terminals and where card payment is offered. SumUp redirects back to
 * /settings?section=bankpayments&code=…&state=… after the merchant approved (Settings.tsx shows
 * this page for it) — this component finishes that on mount and strips the params.
 */
export function SumupConnectionSettings({ saveButtonPortalTarget }: {
    /** Top-bar slot for "Änderungen speichern" (same place as on every settings page); inline when absent. */
    saveButtonPortalTarget?: HTMLElement | null;
}) {
    const { t } = useTranslation();
    const { status, loading, connect, finishConnect, disconnect, setSwitches, setUsePraxis, pairReader, unpairReader } = useSumup();
    // Card terminal pairing form — open for one account at a time.
    const [pairing, setPairing] = useState<{ target: SumupTarget; code: string; name: string; busy: boolean; error: string | null } | null>(null);
    const [confirmReader, setConfirmReader] = useState<SumupReader | null>(null);
    // Unsaved choices on top of the stored status (only the keys the user touched).
    const [edits, setEdits] = useState<Partial<Draft>>({});
    const [saving, setSaving] = useState(false);
    const [justSaved, setJustSaved] = useState(false);
    const [busy, setBusy] = useState<SumupTarget | null>(null);
    const [notice, setNotice] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
    const [confirmTarget, setConfirmTarget] = useState<SumupTarget | null>(null);
    const handledCallback = useRef(false);

    useEffect(() => {
        if (handledCallback.current) return;
        const params = new URLSearchParams(window.location.search);
        const code = params.get("code");
        const state = params.get("state");
        const oauthError = params.get("error");
        if (!state || (!code && !oauthError)) return;
        handledCallback.current = true;

        ["code", "state", "error", "error_description", "scope"].forEach((k) => params.delete(k));
        const rest = params.toString();
        window.history.replaceState({}, "", window.location.pathname + (rest ? `?${rest}` : ""));

        if (oauthError || !code) {
            setNotice({ kind: "error", text: t("sumup.connectCancelled") });
            return;
        }
        finishConnect(code, state).then((r) => {
            if (r.ok) setNotice({ kind: "ok", text: t("sumup.connectSuccess") });
            else setNotice({ kind: "error", text: r.code === "SCOPE_MISSING" ? t("sumup.scopeMissing") : t("sumup.connectFailed") });
        });
    }, [finishConnect, t]);

    const handleConnect = async (target: SumupTarget) => {
        setBusy(target);
        setNotice(null);
        try {
            window.location.href = await connect(target);
        } catch {
            setNotice({ kind: "error", text: t("sumup.connectFailed") });
            setBusy(null);
        }
    };

    // Choices wait for "Änderungen speichern" like every other settings page; connect, pair and
    // remove are actions and happen right away.
    const stored = status ? { usePraxis: status.usePraxis, portal: status.portalEnabled, deskQr: status.deskQrEnabled, terminal: status.terminalEnabled } : null;
    const draft = { ...stored!, ...edits };
    const changedSwitches = (Object.keys(edits) as (keyof Draft)[]).filter((k) => k !== "usePraxis" && stored && edits[k] !== stored[k]) as SumupSwitch[];
    const usePraxisChanged = stored !== null && edits.usePraxis !== undefined && edits.usePraxis !== stored.usePraxis;
    const dirty = changedSwitches.length > 0 || usePraxisChanged;

    const edit = (patch: Partial<Draft>) => { setEdits((e) => ({ ...e, ...patch })); setJustSaved(false); };

    const handleSave = async () => {
        setSaving(true);
        setNotice(null);
        try {
            if (changedSwitches.length) await setSwitches(Object.fromEntries(changedSwitches.map((k) => [k, draft[k]])));
            if (usePraxisChanged) await setUsePraxis(draft.usePraxis);
            setEdits({});
            setJustSaved(true);
        } catch {
            setNotice({ kind: "error", text: t("sumup.saveFailed") });
        } finally {
            setSaving(false);
        }
    };

    const handlePair = async () => {
        if (!pairing) return;
        setPairing({ ...pairing, busy: true, error: null });
        const r = await pairReader(pairing.target, pairing.code, pairing.name.trim());
        if (r.ok) { setPairing(null); return; }
        const error = r.code === "INVALID_CODE" ? t("sumup.pairInvalidCode")
            : r.code === "PAIRING_FAILED" ? t("sumup.pairRejected")
                : t("sumup.pairFailed");
        setPairing((p) => (p ? { ...p, busy: false, error } : p));
    };

    const handleUnpair = async () => {
        const reader = confirmReader;
        setConfirmReader(null);
        if (!reader) return;
        try { await unpairReader(reader.id); } catch { setNotice({ kind: "error", text: t("sumup.unpairFailed") }); }
    };

    const handleDisconnect = async () => {
        const target = confirmTarget;
        setConfirmTarget(null);
        if (!target) return;
        setBusy(target);
        try { await disconnect(target); } finally { setBusy(null); }
    };

    if (loading || !status) {
        return (
            <Section title={t("sumup.title")} description={t("sumup.description")} sticky={false} bodyClassName="body-fin">
                <div className="finapi-row"><span className="skeleton finapi-settings-skeleton" /></div>
            </Section>
        );
    }

    const renderRow = (
        target: SumupTarget,
        label: string,
        hint: string,
        conn: SumupConnectionSummary,
        opts: { unused?: boolean; readOnly?: boolean } = {},
    ) => {
        const live = conn.connected && !conn.needsReconnect && !opts.unused;
        return (
            <div className={`finapi-row sumup-account${opts.unused ? " sumup-account--unused" : ""}`} key={target}>
                <span className={`finapi-settings-dot finapi-settings-dot--${live ? "on" : "off"}`} />
                <div className="sumup-account__text">
                    <span className="finapi-row__status">
                        {label}
                        <span className="finapi-row__sync-time sumup-account__state">
                            {!conn.connected
                                ? t("sumup.notConnected")
                                : opts.unused
                                    ? t("sumup.notUsed")
                                    : conn.needsReconnect
                                        ? t("sumup.needsReconnect")
                                        : t("sumup.connectedAs", { name: conn.merchantName || conn.merchantCode })}
                        </span>
                    </span>
                    <span className="sumup-account__hint">{hint}</span>
                </div>
                {!opts.readOnly && (
                    <div className="finapi-row__actions">
                        {conn.connected && (
                            <button className="finapi-action-btn finapi-action-btn--danger" onClick={() => setConfirmTarget(target)} title={t("sumup.disconnect")} disabled={busy !== null}>
                                <MdLinkOff />
                            </button>
                        )}
                        {(!conn.connected || conn.needsReconnect) && (
                            <button
                                className="finapi-action-btn finapi-action-btn--primary"
                                onClick={() => handleConnect(target)}
                                disabled={busy !== null || opts.unused}
                                title={opts.unused ? t("sumup.ownDisabledHint") : undefined}
                            >
                                <MdLink /> {busy === target ? t("sumup.connecting") : conn.needsReconnect ? t("sumup.reconnect") : t("sumup.connect")}
                            </button>
                        )}
                    </div>
                )}
            </div>
        );
    };

    // SumUp Solo terminals paired with a connected account: staff send an invoice's amount to them.
    const renderTerminals = (target: SumupTarget, conn: SumupConnectionSummary, readOnly: boolean) => {
        if (!status.terminalsConfigured || !conn.connected || conn.needsReconnect) return null;
        const readers = conn.readers ?? [];
        const open = pairing?.target === target ? pairing : null;
        return (
            <div className="sumup-terminals">
                <span className="sumup-terminals__title">{t("sumup.terminalsTitle")}</span>
                {!conn.terminals ? (
                    <p className="sumup-terminals__hint">
                        {t("sumup.terminalsReconnect")}
                        {!readOnly && (
                            <button type="button" className="sumup-terminals__link" onClick={() => handleConnect(target)} disabled={busy !== null}>
                                {t("sumup.reconnect")}
                            </button>
                        )}
                    </p>
                ) : (
                    <>
                        {readers.map((r) => (
                            <div className="sumup-terminals__reader" key={r.id}>
                                <TbCreditCardPay />
                                <span>{r.name}</span>
                                {!readOnly && (
                                    <button
                                        type="button"
                                        className="finapi-action-btn finapi-action-btn--danger"
                                        title={t("sumup.unpairReader")}
                                        aria-label={t("sumup.unpairReader")}
                                        onClick={() => setConfirmReader(r)}
                                    >
                                        <TbTrash />
                                    </button>
                                )}
                            </div>
                        ))}
                        {readers.length === 0 && !open && <p className="sumup-terminals__hint">{t("sumup.terminalsEmpty")}</p>}
                        {!readOnly && (open ? (
                            <form className="sumup-terminals__form" onSubmit={(e) => { e.preventDefault(); void handlePair(); }}>
                                <p className="sumup-terminals__hint">{t("sumup.pairSteps")}</p>
                                <div className="sumup-terminals__fields">
                                    <input
                                        className="settings-input"
                                        aria-label={t("sumup.pairCode")}
                                        placeholder={t("sumup.pairCode")}
                                        value={open.code}
                                        autoComplete="off"
                                        maxLength={12}
                                        onChange={(e) => setPairing({ ...open, code: e.target.value.toUpperCase() })}
                                    />
                                    <input
                                        className="settings-input"
                                        aria-label={t("sumup.pairName")}
                                        placeholder={t("sumup.pairNamePlaceholder")}
                                        value={open.name}
                                        maxLength={60}
                                        onChange={(e) => setPairing({ ...open, name: e.target.value })}
                                    />
                                </div>
                                {open.error && <span className="sumup-terminals__error">{open.error}</span>}
                                <div className="sumup-terminals__actions">
                                    <button type="button" className="finapi-action-btn" onClick={() => setPairing(null)} disabled={open.busy}>
                                        {t("sumup.pairCancel")}
                                    </button>
                                    <button type="submit" className="finapi-action-btn finapi-action-btn--primary" disabled={open.busy || !open.code.trim() || !open.name.trim()}>
                                        {open.busy ? t("sumup.pairing") : t("sumup.pairSubmit")}
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <button
                                type="button"
                                className="sumup-terminals__link"
                                onClick={() => setPairing({ target, code: "", name: readers.length ? "" : t("sumup.pairNameDefault"), busy: false, error: null })}
                            >
                                <TbPlus /> {t("sumup.pairReader")}
                            </button>
                        ))}
                    </>
                )}
            </div>
        );
    };

    return (
        <>
        <Section title={t("sumup.title")} description={t("sumup.description")} sticky={false} bodyClassName="body-fin">
            {notice && (
                <div className={`settings-info-banner settings-info-banner--${notice.kind === "ok" ? "neutral" : "error"}`}>
                    {notice.kind === "ok" ? <TbInfoCircle /> : <TbAlertCircle />}
                    <span>{notice.text}</span>
                </div>
            )}

            {!status.configured ? (
                <div className="settings-info-banner settings-info-banner--neutral">
                    <TbClock />
                    <span><strong>{t("sumup.comingSoon")}</strong> {t("sumup.notAvailableYet")}</span>
                </div>
            ) : (
                <>
                    {renderRow("own", t("sumup.ownAccount"), t("sumup.ownAccountHint"), status.own, { unused: draft.usePraxis })}
                    {!draft.usePraxis && renderTerminals("own", status.own, false)}

                    {/* One or the other: opening this line is the member's choice to use the praxis
                        account — their own connect is disabled while it's open. */}
                    <button
                        type="button"
                        className="sumup-use-praxis"
                        aria-expanded={draft.usePraxis}
                        onClick={() => edit({ usePraxis: !draft.usePraxis })}
                        disabled={saving}
                    >
                        {draft.usePraxis ? <TbChevronDown /> : <TbChevronRight />}
                        {t("sumup.usePraxis")}
                    </button>
                    {draft.usePraxis && (
                        <div className="sumup-use-praxis__body">
                            {renderRow(
                                "praxis",
                                t("sumup.praxisAccount"),
                                status.canManagePraxis || status.praxis.connected ? t("sumup.praxisAccountHint") : t("sumup.praxisAccountAskAdmin"),
                                status.praxis,
                                { readOnly: !status.canManagePraxis },
                            )}
                            {renderTerminals("praxis", status.praxis, !status.canManagePraxis)}
                        </div>
                    )}
                    <p className="finapi-settings-desc sumup-summary">
                        {t("sumup.summary")}
                        <span
                            className="sumup-summary__info"
                            tabIndex={0}
                            data-tooltip={`${t("sumup.howItWorks")} ${t("sumup.feesNotice")}`}
                            aria-label={`${t("sumup.howItWorks")} ${t("sumup.feesNotice")}`}
                        >
                            <TbInfoCircle />
                        </span>
                    </p>
                </>
            )}

        </Section>

        {/* Praxis-wide: where card payment is offered at all. Admin changes them; others see the state. */}
        {status.configured && (
            <Section
                title={t("sumup.switchesTitle")}
                description={status.canManagePraxis ? t("sumup.switchesDesc") : t("sumup.switchesAdminOnly")}
                sticky={false}
            >
                {([
                    ["portal", draft.portal, t("sumup.portalTitle"), t("sumup.portalDesc")],
                    ["deskQr", draft.deskQr, t("sumup.deskQrTitle"), t("sumup.deskQrDesc")],
                    ...(status.terminalsConfigured
                        ? [["terminal", draft.terminal, t("sumup.terminalTitle"),
                            // Stays switchable without a reader (praxis-wide, may be preset) — just says why nothing shows yet.
                            status.praxisHasReaders ? t("sumup.terminalDesc") : `${t("sumup.terminalDesc")} ${t("sumup.terminalNoReader")}`] as const]
                        : []),
                ] as const).map(([key, value, title, desc]) => (
                    <SettingRow
                        key={key}
                        title={<span id={`sumup-switch-${key}`}>{title}</span>}
                        description={desc}
                        disabled={!status.canManagePraxis}
                    >
                        <Toggle
                            value={value}
                            onChange={(v) => edit({ [key]: v })}
                            disabled={!status.canManagePraxis || saving}
                            ariaLabelledBy={`sumup-switch-${key}`}
                        />
                    </SettingRow>
                ))}
            </Section>
        )}

            {confirmTarget && (
                <ConfirmPopup
                    message={t("sumup.confirmDisconnect")}
                    onConfirm={handleDisconnect}
                    onCancel={() => setConfirmTarget(null)}
                />
            )}
            {confirmReader && (
                <ConfirmPopup
                    message={t("sumup.confirmUnpair", { name: confirmReader.name })}
                    onConfirm={handleUnpair}
                    onCancel={() => setConfirmReader(null)}
                />
            )}
            {status.configured && (() => {
                const saveButton = <SaveButton onClick={() => void handleSave()} saved={justSaved && !dirty} dirty={dirty && !saving} />;
                return saveButtonPortalTarget ? createPortal(saveButton, saveButtonPortalTarget) : saveButton;
            })()}
        </>
    );
}
